import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode, type ConfirmSignUpInput } from 'aws-amplify/auth';

const VerificationForm: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email;
    const [confirmationCode, setConfirmationCode] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    useEffect(() => {
        if (!email) {
            navigate('/login');
        }
    }, [email, navigate]);

    // Cooldown timer effect
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => {
                setResendCooldown(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    const handleSignUpConfirmation = async ({ username, confirmationCode }: ConfirmSignUpInput) => {
        if (!username) {
            navigate('/login');
            return;
        }

        setError('');
        setIsSubmitting(true);

        try {
            const { isSignUpComplete, nextStep } = await confirmSignUp({ username, confirmationCode });
            if (isSignUpComplete) {
                navigate('/login', { state: { registrationSuccess: true } });
            } else {
                console.error('Sign-up confirmation not complete. Next step:', nextStep);
                setError('Unable to complete verification. Please try again.');
            }
        } catch (error) {
            if (error instanceof Error) {
                switch ((error as any).name) {
                    case "CodeMismatchException":
                        setError('Invalid verification code. Please try again.');
                        break;
                    case "ExpiredCodeException":
                        setError('Verification code has expired. Please request a new one.');
                        break;
                    case "EmptyConfirmSignUpUsername":
                        navigate('/login');
                        break;
                    default:
                        setError('An error occurred during verification. Please try again.');
                }
            }
            console.error('Error confirming sign-up:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendMfaToken = async (e: React.MouseEvent<HTMLButtonElement>) => {
        if (resendCooldown > 0) return;

        setError('');
        setIsResending(true);

        try {
            await resendSignUpCode({ username: email });
            setResendCooldown(15); // Start 10-second cooldown
        } catch (error) {
            console.error("Error resending verification code:", error);
            setError('Failed to send new verification code. Please try again.');
        } finally {
            setIsResending(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await handleSignUpConfirmation({ username: email, confirmationCode });
    };

    const isSubmitDisabled = confirmationCode.trim() === '' || isSubmitting;
    const isResendDisabled = resendCooldown > 0 || isResending;

    const getResendLabel = () => {
        if (isResending) return "Sending...";
        if (resendCooldown > 0) return `Resend in ${resendCooldown}s`;
        return "Resend it now";
    };
    
    const resendLabel = getResendLabel();

    return (
        <div className="min-h-screen bg-gray-100 flex justify-center items-center">
            <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
                <div className="flex justify-center">
                    <img
                        src="/images/jack-logo.jpg"
                        alt="Jack Logo"
                        style={{ maxWidth: '80%', maxHeight: '30%' }}
                        className="pb-8"
                    />
                </div>
                <h2 className="text-2xl font-bold mb-6 text-center">Verify Your Account</h2>
                <p className="text-gray-600 mb-6">A verification code has been sent to your phone number.</p>
                
                {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                        <label htmlFor="confirmationCode" className="block mb-2 font-bold text-gray-700">
                            Verification Code
                        </label>
                        <input
                            type="text"
                            id="confirmationCode"
                            autoComplete='one-time-code'
                            placeholder="Enter the verification code"
                            value={confirmationCode}
                            onChange={(e) => setConfirmationCode(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                            disabled={isSubmitting}
                        />
                    </div>
                    <button
                        type="submit"
                        className={`w-full text-white font-bold py-2 px-4 rounded-md transition duration-300 
                            ${isSubmitDisabled
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-[#43a4db] hover:bg-[#3786b4]'
                            }`}
                        disabled={isSubmitDisabled}
                    >
                        {isSubmitting ? 'Verifying...' : 'Verify'}
                    </button>
                </form>

                <div className="mt-4 text-center">
                    <span className="text-gray-600">Didn't receive a verification code? </span>
                    <button
                        type="button"
                        onClick={handleResendMfaToken}
                        disabled={isResendDisabled}
                        className={`text-blue-600 hover:underline ${isResendDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {resendLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VerificationForm;